import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Award, Info, CheckCircle, Circle, ChevronRight, Home, Trophy, Zap, Dumbbell, Activity, Edit, Save, X, Plus, Minus, User } from 'lucide-react';

// Romy's workout plans - simplified for a beginner
const romyWorkoutPlans = [
  {
    id: 'planA',
    title: 'Plan A: Beine & Po',
    description: 'Fokus auf Bein- und Gesäßmuskulatur für maximale Kalorienverbrennung',
    exercises: [
      { id: 'a1', name: 'Kniebeugen mit Langhantel (24kg)', sets: 3, reps: '10-12', difficulty: 'mittel', target: 'Beine & Po',
        instructions: [
          'Langhantel (20kg) mit 2kg Gewichten auf oberem Rücken/Trapezmuskel legen',
          'Im Squat-Rack die richtige Höhe einstellen',
          'Füße etwas breiter als schulterbreit aufstellen',
          'Po nach hinten schieben (wie aufs Sofa setzen)',
          'Rücken gerade halten und Blick nach vorne',
          'Knie nicht über Zehenspitzen',
          'Tief genug absenken (Oberschenkel parallel zum Boden)',
          'Fersen bleiben am Boden'
        ]
      },
      { id: 'a2', name: 'Hüftbrücke mit Gummiband', sets: 3, reps: '15', difficulty: 'mittel', target: 'Po & Oberschenkelrückseite',
        instructions: [
          'Auf Rücken liegen, Knie gebeugt',
          'Gummiband knapp oberhalb der Knie platzieren',
          'Füße hüftbreit aufstellen',
          'Po und Bauchmuskeln anspannen',
          'Hüfte nach oben drücken bis Oberkörper und Oberschenkel eine Linie bilden',
          'Knie während der gesamten Übung leicht nach außen drücken (gegen Widerstand)',
          'Oben 2 Sekunden halten und maximale Spannung im Po erzeugen',
          'Kontrolliert absenken, ohne den Boden zu berühren (Spannung halten)'
        ]
      },
      { id: 'a3', name: 'Ausfallschritte mit Gewichten', sets: 3, reps: '10 pro Bein', difficulty: 'mittel', target: 'Beine, Po & Balance',
        instructions: [
          'In jeder Hand eine 3kg Kurzhantel halten',
          'Aufrecht stehen, Füße hüftbreit',
          'Großen Schritt nach vorne machen',
          'Vorderes Knie über dem Knöchel, nicht darüber hinaus',
          'Hinteres Knie Richtung Boden senken (ohne Bodenkontakt)',
          'Beide Knie sollten etwa 90 Grad gebeugt sein',
          'Über vorderes Bein zurück in die Ausgangsposition drücken',
          'Alle Wiederholungen mit einem Bein, dann wechseln'
        ]
      },
      { id: 'a4', name: 'Bein-Abduktion mit Gummiband', sets: 3, reps: '12-15 pro Seite', difficulty: 'leicht', target: 'Äußere Hüftmuskulatur',
        instructions: [
          'Gummiband knapp oberhalb der Knöchel oder Knie platzieren',
          'Seitlich an Wand/Stütze abstützen oder im Stehen',
          'Füße hüftbreit, leichte Beugung in Knien',
          'Bauchmuskeln anspannen, Oberkörper aufrecht',
          'Ein Bein zur Seite heben, gegen den Widerstand des Bandes',
          'Hüfte nicht nach vorn oder hinten kippen',
          'Kurz halten, dann kontrolliert zurück',
          'Nach einer Seite alle Wiederholungen, dann wechseln'
        ]
      },
      { id: 'a5', name: 'Bulgarian Split Squats', sets: 3, reps: '10-12 pro Bein', difficulty: 'mittel', target: 'Beine, Po & Stabilität',
        instructions: [
          'Hinteres Bein auf einer erhöhten Fläche (Bank oder Stuhl) ablegen',
          'Vorderes Bein etwa einen Schritt vor der erhöhten Fläche',
          'Oberkörper aufrecht, Blick nach vorne',
          'Vorderes Knie beugen und in die Kniebeuge gehen',
          'Vorderes Knie bleibt über oder hinter der Zehenspitze',
          'Tief genug gehen, bis Oberschenkel etwa parallel zum Boden',
          'Über vorderes Bein zurück nach oben drücken',
          'Für mehr Intensität: 3kg Hanteln in beiden Händen halten'
        ]
      }
    ],
    bonusExercise: {
      id: 'a-bonus1',
      name: 'BONUS 1: Mountain Climbers',
      sets: 3,
      reps: '30 Sekunden',
      difficulty: 'mittel',
      target: 'Ganzkörper & Herz-Kreislauf',
      instructions: [
        'In Liegestützposition beginnen (Arme gestreckt)',
        'Rücken gerade halten, Körper bildet eine Linie',
        'Abwechselnd Knie Richtung Brust ziehen',
        'Dynamisch aber kontrolliert arbeiten',
        'Hüfte nicht anheben (keine Pikeposition)',
        'Bauchmuskeln während der gesamten Übung angespannt halten',
        'Versuche, 30 Sekunden durchzuhalten',
        'Perfekt für Kalorienverbrennung am Ende des Trainings!'
      ]
    },
    bonusExercise2: {
      id: 'a-bonus2',
      name: 'BONUS 2: Jump Squats',
      sets: 2,
      reps: '10-12',
      difficulty: 'mittel',
      target: 'Beine, Po & Herz-Kreislauf',
      instructions: [
        'Füße schulterbreit aufstellen',
        'In eine tiefe Kniebeuge gehen (wie bei normalen Kniebeugen)',
        'Explosiv nach oben springen, Arme zur Unterstützung nach oben schwingen',
        'Weich und kontrolliert landen, direkt in die nächste Kniebeuge übergehen',
        'Knie leicht gebeugt bei der Landung, um Gelenke zu schonen',
        'Körperspannung die ganze Zeit halten',
        'Für Anfänger: weniger tief in die Hocke gehen',
        'Ideal für Kraftentwicklung und zusätzliche Kalorienverbrennung'
      ]
    }
  },
  {
    id: 'planB',
    title: 'Plan B: Arme & Schultern',
    description: 'Fokus auf Oberkörper mit Push- und Pull-Bewegungen',
    exercises: [
      { id: 'b1', name: 'Hang High Pulls mit Kurzhantel (3kg)', sets: 3, reps: '10-12', difficulty: 'mittel', target: 'Schultern & oberer Rücken',
        instructions: [
          'Kurzhantel mit beiden Händen vor dem Körper halten',
          'Leicht in die Knie gehen, Oberkörper leicht nach vorne geneigt',
          'Ellbogen außen, höher als Handgelenke',
          'Hantel explosiv nach oben ziehen (etwa bis Kinnhöhe)',
          'Ellbogen zeigen nach außen/oben',
          'Schultern nicht hochziehen',
          'Langsam und kontrolliert zurück in Ausgangsposition',
          'Auf korrekte Technik achten, nicht zu schwer wählen'
        ]
      },
      { id: 'b2', name: 'Schulterdrücken mit Kurzhanteln (3kg)', sets: 3, reps: '10-12', difficulty: 'mittel', target: 'Schultern & Arme',
        instructions: [
          'Aufrecht sitzen oder stehen',
          'Kurzhanteln auf Schulterhöhe halten',
          'Handflächen zeigen nach vorne oder zueinander',
          'Bauchmuskeln anspannen, Rücken gerade',
          'Arme nach oben strecken, bis Ellbogen fast gestreckt sind',
          'Kurz halten, dann kontrolliert wieder senken',
          'Ellbogen nicht ganz durchstrecken (leichte Spannung halten)',
          'Nicht ins Hohlkreuz fallen'
        ]
      },
      { id: 'b3', name: 'Elevated Push-ups (erhöhte Liegestütze)', sets: 3, reps: '8-10', difficulty: 'mittel', target: 'Brust, Schultern & Arme',
        instructions: [
          'Hände auf erhöhter Oberfläche (z.B. Squat-Rack oder Bank) platzieren',
          'Körper bildet eine gerade Linie von Kopf bis Fuß',
          'Hände etwas breiter als schulterbreit',
          'Ellbogen während der Bewegung etwa 45° vom Körper',
          'Brust zur erhöhten Fläche senken',
          'Durch Arme wieder nach oben drücken',
          'Bauchmuskeln und Po während der gesamten Übung anspannen',
          'Je niedriger die Erhöhung, desto schwieriger die Übung'
        ]
      },
      { id: 'b4', name: 'Rudern mit Widerstandsband', sets: 3, reps: '12-15', difficulty: 'mittel', target: 'Mittlerer Rücken & Bizeps',
        instructions: [
          'Widerstandsband um festen Punkt wickeln (z.B. Squat-Rack)',
          'Mit leicht gebeugten Knien und geradem Rücken hinstellen',
          'Oberkörper leicht nach vorne neigen',
          'Arme ausstrecken, Band greifen',
          'Ellbogen nah am Körper nach hinten ziehen',
          'Schulterblätter zusammenziehen',
          'Kurz halten, dann kontrolliert wieder ausstrecken',
          'Rücken während der gesamten Übung gerade halten'
        ]
      },
      { id: 'b5', name: 'Trizeps-Pushdowns mit Band', sets: 3, reps: '12-15', difficulty: 'mittel', target: 'Trizeps',
        instructions: [
          'Band an hohem Punkt befestigen (z.B. Pull-up-Bar)',
          'Mit Gesicht zum Ankerpunkt stehen',
          'Ellbogen eng am Körper, im 90° Winkel',
          'Unterarme nach unten drücken, bis Arme fast gestreckt',
          'Nur die Unterarme bewegen, Oberarme fixiert halten',
          'Kurz halten und Trizeps anspannen',
          'Langsam in Ausgangsposition zurückkehren',
          'Für maximalen Effekt Bewegung nicht ganz oben beenden (Spannung halten)'
        ]
      }
    ],
    bonusExercise: {
      id: 'b-bonus1',
      name: 'BONUS 1: Band Pull Aparts',
      sets: 3,
      reps: '15',
      difficulty: 'leicht',
      target: 'Hintere Schultern & oberer Rücken',
      instructions: [
        'Widerstandsband mit ausgestreckten Armen vor der Brust halten',
        'Arme auf Schulterhöhe',
        'Band auseinanderziehen, bis Arme fast seitlich gestreckt sind',
        'Schulterblätter zusammenziehen',
        'Position kurz halten und maximale Spannung erzeugen',
        'Langsam und kontrolliert zurück',
        'Während der gesamten Übung Spannung im Band halten',
        'Perfekt für Haltungsverbesserung und als Ausgleich zu Brustübungen'
      ]
    },
    bonusExercise2: {
      id: 'b-bonus2',
      name: 'BONUS 2: Lateral Raises',
      sets: 2,
      reps: '12-15',
      difficulty: 'mittel',
      target: 'Seitliche Schultern',
      instructions: [
        'Aufrecht stehen mit je einer 3kg Kurzhantel in jeder Hand',
        'Arme seitlich am Körper, Handflächen zum Körper',
        'Ellbogen leicht gebeugt halten (nicht komplett gestreckt)',
        'Arme seitlich anheben bis etwa Schulterhöhe',
        'Kurz in Endposition halten',
        'Langsam und kontrolliert absenken',
        'Nicht mit Schwung arbeiten',
        'Ideal für definierte, rundere Schultern'
      ]
    }
  },
  {
    id: 'planC',
    title: 'Plan C: Bauch & Rücken',
    description: 'Fokus auf Core-Stabilität und Rückenkraft',
    exercises: [
      { id: 'c1', name: 'Unterarmstütz Variationen', sets: 3, reps: '45 Sek gesamt', difficulty: 'mittel', target: 'Tiefe Bauchmuskeln & Körperstabilität',
        instructions: [
          'Auf Unterarmen und Zehenspitzen stützen',
          'Körper bildet eine gerade Linie von Kopf bis Fuß',
          'Bauchmuskeln und Po anspannen',
          'Position 30 Sekunden halten',
          'Dann 15 Sekunden abwechselnd ein Knie zum Ellbogen ziehen',
          'Hüfte nicht absenken oder anheben',
          'Blick nach unten zum Boden, Nacken in neutraler Position',
          'Gleichmäßig atmen, nicht die Luft anhalten'
        ]
      },
      { id: 'c2', name: 'Seitliche Rotate Pulls', sets: 3, reps: '10 pro Seite', difficulty: 'mittel', target: 'Schräge Bauchmuskeln & Taille',
        instructions: [
          'Widerstandsband auf Brusthöhe an Squat-Rack befestigen',
          'Seitlich zum Befestigungspunkt stellen',
          'Füße etwa schulterbreit auseinander',
          'Band mit beiden Händen vor der Brust greifen',
          'Körper vom Befestigungspunkt weg rotieren',
          'Arme dabei gestreckt halten',
          'Drehung kommt aus der Taille, nicht aus den Armen',
          'Kontrolliert zurückführen und Seite nach Satz wechseln'
        ]
      },
      { id: 'c3', name: 'Beinheben im Liegen', sets: 3, reps: '12-15', difficulty: 'mittel', target: 'Unterer Bauch',
        instructions: [
          'Auf dem Rücken liegen, Arme neben dem Körper',
          'Unterer Rücken fest in den Boden gedrückt',
          'Beine gestreckt anheben bis etwa 45° Winkel',
          'Langsam und kontrolliert senken, ohne den Boden zu berühren',
          'Bei Rückenbeschwerden: Knie leicht beugen',
          'Bauchmuskeln während der gesamten Übung angespannt halten',
          'Nicht mit Schwung arbeiten',
          'Atmung: Beim Heben ausatmen, beim Senken einatmen'
        ]
      },
      { id: 'c4', name: 'Banded Good Mornings', sets: 3, reps: '12', difficulty: 'mittel', target: 'Unterer Rücken & Beinrückseite',
        instructions: [
          'Auf Band stehen, Schlaufe über Nacken/Schultern legen',
          'Füße schulterbreit, leicht gebeugte Knie',
          'Bauchmuskeln anspannen, Brust raus',
          'Hüfte nach hinten schieben, Oberkörper nach vorne neigen',
          'Rücken durchgehend gerade halten (nicht rund werden)',
          'Bis etwa 90 Grad beugen (parallel zum Boden)',
          'Mit der Kraft des unteren Rückens und der Beinrückseite aufrichten',
          'Idealer Ersatz für Kreuzheben mit Langhantel'
        ]
      },
      { id: 'c5', name: 'Russian Twists', sets: 3, reps: '15 pro Seite', difficulty: 'mittel', target: 'Schräge Bauchmuskeln',
        instructions: [
          'Im Sitzen, Knie gebeugt, Füße leicht vom Boden angehoben',
          'Oberkörper leicht nach hinten geneigt',
          'Hände vor der Brust zusammenhalten oder 3kg Kurzhantel halten',
          'Oberkörper nach rechts drehen, dann nach links',
          'So weit wie möglich drehen, ohne Gleichgewicht zu verlieren',
          'Unterer Rücken bleibt gerade, nicht zusammenfallen',
          'Für Anfänger: Füße auf dem Boden lassen',
          'Für mehr Intensität: Füße höher anheben'
        ]
      }
    ],
    bonusExercise: {
      id: 'c-bonus1',
      name: 'BONUS 1: Bird-Dog',
      sets: 3,
      reps: '10 pro Seite',
      difficulty: 'mittel',
      target: 'Tiefe Rückenmuskulatur & Körperstabilität',
      instructions: [
        'Auf allen Vieren beginnen (Hände unter Schultern, Knie unter Hüften)',
        'Rücken in neutraler Position, Blick zum Boden',
        'Gleichzeitig gegenüberliegende Arm und Bein ausstrecken',
        'Rechter Arm nach vorne, linkes Bein nach hinten (und umgekehrt)',
        'Arm und Bein auf einer Höhe mit dem Rücken halten',
        'Position kurz halten, dann zurück in Ausgangsposition',
        'Seiten abwechseln',
        'Hervorragende Übung für Rückenstabilität und Vorbeugung von Rückenschmerzen'
      ]
    },
    bonusExercise2: {
      id: 'c-bonus2',
      name: 'BONUS 2: Superman Holds',
      sets: 2,
      reps: '3 Holds × 10 Sek',
      difficulty: 'mittel',
      target: 'Unterer Rücken & posteriore Kette',
      instructions: [
        'Bauchlage, Arme nach vorne ausgestreckt (Superman-Position)',
        'Bauchmuskeln anspannen, Beine geschlossen',
        'Arme, Oberkörper und Beine gleichzeitig leicht anheben',
        'Blick nach unten/leicht nach vorne gerichtet (nicht Nacken überstrecken)',
        'Position 10 Sekunden halten, dann kurz entspannen',
        'Nach 2-3 Sekunden nächsten Hold beginnen',
        'Intensität steigern durch längeres Halten oder höheres Anheben',
        'Perfekt für Stärkung des unteren Rückens und zur Haltungsverbesserung'
      ]
    }
  }
];

// David's workout plans - based on the provided training routine
const davidWorkoutPlans = [
  {
    id: 'powerCleanDay',
    title: 'Power Clean Focus',
    description: 'Fokus auf Power Clean, Clean Pulls, Front Squat und Overhead Press',
    exercises: [
      { id: 'pc1', name: 'Power Clean', sets: 3, reps: '5', weight: '85kg', difficulty: 'schwer', target: 'Ganzkörper',
        instructions: [
          'Füße schulterbreit',
          'Hantel auf dem Boden',
          'Hüften tiefer als Schultern',
          'Rücken gerade',
          'Explosiv nach oben ziehen',
          'Unter die Hantel gehen',
          'Hantel auf Schultern fangen'
        ],
        progression: [
          { week: 1, weight: '70kg, 75kg, 80kg' },
          { week: 2, weight: '75kg, 80kg, 85kg' },
          { week: 3, weight: '80kg, 85kg, 90kg' },
          { week: 4, weight: '85kg, 90kg, 95kg' }
        ]
      },
      { id: 'cp1', name: 'Clean Pulls', sets: 3, reps: '3', weight: '105kg', difficulty: 'schwer', target: 'Rücken & Beine',
        instructions: [
          'Wie Power Clean',
          'Aber nicht unter die Hantel gehen',
          'Vollständig aufrichten',
          'Schultern nach hinten ziehen',
          'Kontrolliert absenken'
        ],
        progression: [
          { week: 1, weight: '90kg, 95kg, 100kg' },
          { week: 2, weight: '95kg, 100kg, 105kg' },
          { week: 3, weight: '100kg, 105kg, 110kg' },
          { week: 4, weight: '105kg, 110kg, 115kg' }
        ]
      },
      { id: 'fs1', name: 'Front Squat', sets: 3, reps: '5', weight: '95kg', difficulty: 'schwer', target: 'Beine & Core',
        instructions: [
          'Hantel auf Schultern/Schlüsselbein',
          'Ellbogen hoch',
          'Füße schulterbreit',
          'Knie in Richtung der Füße',
          'Tief absenken',
          'Rücken gerade halten',
          'Kraftvoll nach oben drücken'
        ],
        progression: [
          { week: 1, weight: '80kg' },
          { week: 2, weight: '85kg' },
          { week: 3, weight: '90kg' },
          { week: 4, weight: '95kg' }
        ]
      },
      { id: 'op1', name: 'Overhead Press', sets: 3, reps: '5', weight: '47.5kg', difficulty: 'mittel', target: 'Schultern & Arme',
        instructions: [
          'Hantel auf Schultern',
          'Füße schulterbreit',
          'Core anspannen',
          'Hantel über Kopf drücken',
          'Arme vollständig strecken',
          'Kontrolliert absenken'
        ],
        progression: [
          { week: 1, weight: '40kg' },
          { week: 2, weight: '42.5kg' },
          { week: 3, weight: '45kg' },
          { week: 4, weight: '47.5kg' }
        ]
      },
      { id: 'pl1', name: 'Plank', sets: 3, reps: '60 sec', difficulty: 'mittel', target: 'Core & Stabilität',
        instructions: [
          'Unterarme auf dem Boden',
          'Körper als gerade Linie',
          'Core anspannen',
          'Schultern vom Nacken weg',
          'Normal atmen',
          'Position halten'
        ],
        progression: [
          { week: 1, duration: '45 sec' },
          { week: 2, duration: '50 sec' },
          { week: 3, duration: '55 sec' },
          { week: 4, duration: '60 sec' }
        ]
      }
    ],
    bonusExercise: {
      id: 'pc-bonus1',
      name: 'BONUS 1: Weighted Pull-ups',
      sets: 3,
      reps: '5',
      weight: 'BW+5kg',
      difficulty: 'schwer',
      target: 'Oberer Rücken, Bizeps & Grip Strength',
      instructions: [
        'Gewichtsplatten an Gürtel oder zwischen den Beinen fixieren',
        'Pull-up-Bar mit etwas mehr als schulterbreitem Griff fassen',
        'Handflächen zeigen nach vorne (Pronation)',
        'Schulterblätter zusammenziehen und stabilisieren',
        'Kinn über die Stange ziehen',
        'Kurz halten, dann kontrolliert absenken',
        'Arme zwischen Wiederholungen vollständig strecken',
        'Die optimale Ergänzung zu deinem Overhead Press'
      ],
      progression: [
        { week: 1, weight: 'BW+2.5kg' },
        { week: 2, weight: 'BW+5kg' },
        { week: 3, weight: 'BW+7.5kg' },
        { week: 4, weight: 'BW+10kg' }
      ]
    },
    bonusExercise2: {
      id: 'pc-bonus2',
      name: 'BONUS 2: Barbell Z-Press',
      sets: 2,
      reps: '5-8',
      weight: '40kg',
      difficulty: 'schwer',
      target: 'Schultern, Core & Stabilisatoren',
      instructions: [
        'Im Sitzen auf dem Boden mit ausgestreckten Beinen',
        'Langhantel (nur 20kg Stange oder leichte Gewichte) auf Schlüsselbeinlinie',
        'Rumpf aktiv anspannen, Rücken gerade',
        'Hantel ohne Unterstützung der Beine/Füße über den Kopf drücken',
        'Ellbogen unter der Stange halten',
        'Kontrolle und Stabilität sind wichtiger als Gewicht',
        'Fordert Core und Schultern gleichzeitig heraus',
        'Deckt Mobilitätsdefizite in Hüfte und Schultern auf'
      ],
      progression: [
        { week: 1, weight: '30kg' },
        { week: 2, weight: '35kg' },
        { week: 3, weight: '37.5kg' },
        { week: 4, weight: '40kg' }
      ]
    }
  },
  {
    id: 'deadliftDay',
    title: 'Deadlift Focus',
    description: 'Fokus auf Deadlift, Barbell Row und Unterstützungsübungen',
    exercises: [
      { id: 'dl1', name: 'Deadlift', sets: 3, reps: '5,3,1', weight: '130kg, 140kg, 150kg', difficulty: 'schwer', target: 'Ganzkörper',
        instructions: [
          'Füße hüftbreit',
          'Hantel über Mittelfuß',
          'Hüften zurück, Knie beugen',
          'Rücken gerade',
          'Brust raus',
          'Hantel eng am Körper hochziehen',
          'Hüften nach vorne schieben beim Aufrichten'
        ],
        progression: [
          { week: 1, weight: '100kg, 120kg, 130kg' },
          { week: 2, weight: '110kg, 125kg, 135kg' },
          { week: 3, weight: '120kg, 130kg, 140kg' },
          { week: 4, weight: '130kg, 140kg, 150kg' }
        ]
      },
      { id: 'br1', name: 'Barbell Row', sets: 3, reps: '5', weight: '85kg', difficulty: 'mittel', target: 'Oberer Rücken',
        instructions: [
          'Füße schulterbreit',
          'Knie leicht gebeugt',
          'Oberkörper nach vorne geneigt',
          'Rücken gerade',
          'Hantel zum unteren Bauch ziehen',
          'Ellbogen nach hinten',
          'Kontrolliert absenken'
        ],
        progression: [
          { week: 1, weight: '70kg' },
          { week: 2, weight: '75kg' },
          { week: 3, weight: '80kg' },
          { week: 4, weight: '85kg' }
        ]
      },
      { id: 'dc1', name: 'Dumbbell Curl', sets: 3, reps: '10', weight: '13kg', difficulty: 'leicht', target: 'Bizeps',
        instructions: [
          'Aufrecht stehen',
          'Ellbogen am Körper fixiert',
          'Handflächen nach vorne',
          'Hantel zum Schulter curlen',
          'Oberarme nicht bewegen',
          'Kontrolliert absenken'
        ],
        progression: [
          { week: 1, weight: '13kg' },
          { week: 2, weight: '13kg' },
          { week: 3, weight: '13kg' },
          { week: 4, weight: '13kg' }
        ]
      },
      { id: 'bpa1', name: 'Band Pull-Aparts', sets: 3, reps: '15', difficulty: 'leicht', target: 'Hintere Schultern',
        instructions: [
          'Band auf Schulterhöhe',
          'Arme ausgestreckt',
          'Band auseinander ziehen',
          'Schulterblätter zusammen',
          'Kontrolliert zurück'
        ]
      }
    ],
    bonusExercise: {
      id: 'dl-bonus1',
      name: 'BONUS 1: Romanian Deadlift (RDL)',
      sets: 3,
      reps: '8',
      weight: '100kg',
      difficulty: 'schwer',
      target: 'Hamstrings, Gesäß & unterer Rücken',
      instructions: [
        'Langhantel im Stehen auf Hüfthöhe halten',
        'Füße hüftbreit, Knie leicht gebeugt',
        'Rücken während der gesamten Bewegung gerade halten',
        'Hüfte nach hinten schieben (wie Türe mit Po schließen)',
        'Oberkörper absenken bis Hantel unterhalb der Knie',
        'Hamstrings sollten deutlich gedehnt werden',
        'Mit Kraft aus Gesäß und Hamstrings aufrichten',
        'Perfekte Übung für die Kraftentwicklung in der posterioren Kette'
      ],
      progression: [
        { week: 1, weight: '85kg' },
        { week: 2, weight: '90kg' },
        { week: 3, weight: '95kg' },
        { week: 4, weight: '100kg' }
      ]
    },
    bonusExercise2: {
      id: 'dl-bonus2',
      name: 'BONUS 2: Heavy Farmer\'s Walks',
      sets: 2,
      reps: '20m × 3 Runden',
      weight: '40kg pro Hand',
      difficulty: 'schwer',
      target: 'Grip, Core, Trapezius & mentale Stärke',
      instructions: [
        'Schwere Kurzhanteln oder Gewichtsplatten in beide Hände nehmen',
        'Schultern zurück und nach unten ziehen (nicht hochziehen)',
        'Brust raus, Bauchmuskeln anspannen',
        'Aufrecht gehen, Blick nach vorne',
        'Kurze, kontrollierte Schritte machen',
        '20 Meter gehen, kurz absetzen, wiederholen',
        'Pro Satz 3 Runden absolvieren',
        'Fordert Griffkraft und stabilisierende Muskeln, die für Deadlifts wichtig sind'
      ],
      progression: [
        { week: 1, weight: '30kg pro Hand' },
        { week: 2, weight: '35kg pro Hand' },
        { week: 3, weight: '37.5kg pro Hand' },
        { week: 4, weight: '40kg pro Hand' }
      ]
    }
  },
  {
    id: 'squatDay',
    title: 'Squat Focus',
    description: 'Fokus auf Clean Squat, Lunges und Core-Training',
    exercises: [
      { id: 'cs1', name: 'Clean Squat', sets: 3, reps: '5', weight: '104kg', difficulty: 'schwer', target: 'Beine & Po',
        instructions: [
          'Hantel auf Schultern',
          'Füße schulterbreit',
          'Knie in Richtung Zehen',
          'Tief absenken',
          'Rücken gerade halten',
          'Kraftvoll nach oben'
        ],
        progression: [
          { week: 1, weight: '70kg, 90kg' },
          { week: 2, weight: '95kg' },
          { week: 3, weight: '100kg' },
          { week: 4, weight: '104kg' }
        ]
      },
      { id: 'lu1', name: 'Lunges', sets: 3, reps: '10 pro Bein', weight: '17kg', difficulty: 'mittel', target: 'Beine & Po',
        instructions: [
          'Aufrecht stehen',
          'Schritt nach vorne',
          'Vorderes Knie 90°',
          'Hinteres Knie fast bis Boden',
          'Oberkörper aufrecht',
          'Zurück in Ausgangsposition'
        ],
        progression: [
          { week: 1, weight: '17kg' },
          { week: 2, weight: '17kg' },
          { week: 3, weight: '17kg' },
          { week: 4, weight: '17kg' }
        ]
      },
      { id: 'op2', name: 'Overhead Press', sets: 3, reps: '5', weight: '50kg', difficulty: 'mittel', target: 'Schultern & Arme',
        instructions: [
          'Hantel auf Schultern',
          'Füße schulterbreit',
          'Core anspannen',
          'Hantel über Kopf drücken',
          'Arme vollständig strecken',
          'Kontrolliert absenken'
        ],
        progression: [
          { week: 1, weight: '42.5kg' },
          { week: 2, weight: '45kg' },
          { week: 3, weight: '47.5kg' },
          { week: 4, weight: '50kg' }
        ]
      },
      { id: 'rt1', name: 'Russian Twist', sets: 3, reps: '20', weight: '13kg', difficulty: 'mittel', target: 'Seitlicher Bauch',
        instructions: [
          'Sitzende Position',
          'Knie gebeugt',
          'Oberkörper leicht zurückgelehnt',
          'Gewicht vor der Brust',
          'Oberkörper zur Seite drehen',
          'Seite wechseln',
          'Kontrolliert bewegen'
        ],
        progression: [
          { week: 1, weight: '13kg' },
          { week: 2, weight: '13kg' },
          { week: 3, weight: '13kg' },
          { week: 4, weight: '13kg' }
        ]
      }
    ],
    bonusExercise: {
      id: 'sq-bonus1',
      name: 'BONUS 1: Barbell Hip Thrust',
      sets: 3,
      reps: '8',
      weight: '100kg',
      difficulty: 'schwer',
      target: 'Gesäßmuskulatur & hintere Oberschenkel',
      instructions: [
        'Oberer Rücken an eine Bank gelehnt, Füße schulterbreit',
        'Langhantel über Hüfte legen (mit Polster für Komfort)',
        'Tiefposition: Hüfte abgesenkt, Oberschenkel etwa parallel zum Boden',
        'Gesäß anspannen und Hüfte nach oben drücken',
        'Endposition: Körper bildet gerade Linie von Schulter bis Knie',
        'Position kurz halten und maximale Kontraktion im Gesäß erzeugen',
        'Kontrolliert absenken',
        'Perfekte Ergänzung zu Kniebeugen für maximalen Muskelaufbau im Unterkörper'
      ],
      progression: [
        { week: 1, weight: '85kg' },
        { week: 2, weight: '90kg' },
        { week: 3, weight: '95kg' },
        { week: 4, weight: '100kg' }
      ]
    },
    bonusExercise2: {
      id: 'sq-bonus2',
      name: 'BONUS 2: Standing Calf Raises',
      sets: 3,
      reps: '15 pro Bein',
      weight: '25kg',
      difficulty: 'mittel',
      target: 'Waden & Fußstabilisatoren',
      instructions: [
        'Auf einer niedrigen Stufe oder Platte stehen, Fersen frei',
        '25kg Platte in einer Hand halten (für einbeinige Version)',
        'Am Squat-Rack oder Wand für Balance abstützen',
        'Ferse weit nach unten sinken lassen (volle Dehnung)',
        'Bis in den Zehenstand hochdrücken (volle Kontraktion)',
        'Oben 1-2 Sekunden halten',
        'Für gleichmäßige Entwicklung jeden Satz einbeinig ausführen',
        'Starke Waden verbessern Kraftübertragung bei Kniebeugen und Kreuzheben'
      ],
      progression: [
        { week: 1, weight: '10kg' },
        { week: 2, weight: '15kg' },
        { week: 3, weight: '20kg' },
        { week: 4, weight: '25kg' }
      ]
    }
  }
];

// Hauptkomponente
const ExerciseEditForm = ({ exercise, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: exercise.name,
    sets: exercise.sets,
    reps: exercise.reps,
    weight: exercise.weight || '',
    difficulty: exercise.difficulty
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(exercise.id, formData);
  };

  return (
      <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl max-w-md w-full p-5 shadow-2xl border border-gray-700">
          <h3 className="text-lg font-bold mb-4 text-indigo-300">Übung bearbeiten</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-gray-300 block mb-1">Name</label>
              <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 block mb-1">Sätze</label>
                <input
                    type="number"
                    name="sets"
                    value={formData.sets}
                    onChange={handleChange}
                    className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600"
                />
              </div>

              <div>
                <label className="text-gray-300 block mb-1">Wiederholungen</label>
                <input
                    type="text"
                    name="reps"
                    value={formData.reps}
                    onChange={handleChange}
                    className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="text-gray-300 block mb-1">Gewicht (optional)</label>
              <input
                  type="text"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  placeholder="z.B. 20kg"
                  className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600"
              />
            </div>

            <div>
              <label className="text-gray-300 block mb-1">Schwierigkeit</label>
              <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600"
              >
                <option value="leicht">Leicht</option>
                <option value="mittel">Mittel</option>
                <option value="schwer">Schwer</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 rounded border border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Abbrechen
              </button>

              <button
                  type="submit"
                  className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Speichern
              </button>
            </div>
          </form>
        </div>
      </div>
  );
};

// Hauptkomponente
const WorkoutApp = () => {
  // States
  const [currentView, setCurrentView] = useState('home'); // 'home', 'workout', 'tracker', 'tips'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [completedExercises, setCompletedExercises] = useState({});
  const [showExerciseDetails, setShowExerciseDetails] = useState(null);
  const [workoutHistory, setWorkoutHistory] = useState({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [showUserSelect, setShowUserSelect] = useState(false);

  // Load data from localStorage and URL path
  useEffect(() => {
    // Check URL for user path
    const pathSegments = window.location.pathname.split('/');
    let userFromPath = pathSegments[pathSegments.length - 1].toLowerCase();

    // If path ends with slash or is empty, check if there's a previous user
    if (userFromPath === '' || userFromPath === '/') {
      const savedUser = localStorage.getItem('gymapp_username');
      userFromPath = savedUser || '';
    }

    // Set up user profile
    setUpUserProfile(userFromPath);

    // If no user is set, show user selection
    if (!userFromPath) {
      setShowUserSelect(true);
    }
  }, []);

  // Set up user profile based on name
  const setUpUserProfile = (name) => {
    if (!name) return;

    const lowerName = name.toLowerCase();

    // Save user to localStorage and update state
    localStorage.setItem('gymapp_username', lowerName);
    setUserName(lowerName);

    // Load user-specific data
    const userCompletedExercises = localStorage.getItem(`${lowerName}_completedExercises`);
    const userWorkoutHistory = localStorage.getItem(`${lowerName}_workoutHistory`);
    const userPlans = localStorage.getItem(`${lowerName}_plans`);

    if (userCompletedExercises) {
      setCompletedExercises(JSON.parse(userCompletedExercises));
    } else {
      setCompletedExercises({});
    }

    if (userWorkoutHistory) {
      setWorkoutHistory(JSON.parse(userWorkoutHistory));
    } else {
      setWorkoutHistory({});
    }

    // Set user profile and workout plans
    if (lowerName === 'romy') {
      setUserProfile({
        name: 'Romy',
        level: 'Beginner',
        goal: 'Gewichtsreduktion',
        color: 'purple'
      });

      // Load saved plans or use defaults
      if (userPlans) {
        setWorkoutPlans(JSON.parse(userPlans));
      } else {
        setWorkoutPlans(romyWorkoutPlans);
      }
    } else if (lowerName === 'david') {
      setUserProfile({
        name: 'David',
        level: 'Advanced',
        goal: 'Strength',
        color: 'blue'
      });

      // Load saved plans or use defaults
      if (userPlans) {
        setWorkoutPlans(JSON.parse(userPlans));
      } else {
        setWorkoutPlans(davidWorkoutPlans);
      }
    } else {
      // Generic user profile
      setUserProfile({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        level: 'Custom',
        goal: 'Fitness',
        color: 'green'
      });

      // For custom users, default to Romy's plan but allow customization
      if (userPlans) {
        setWorkoutPlans(JSON.parse(userPlans));
      } else {
        setWorkoutPlans(romyWorkoutPlans);
      }
    }

    // Update URL without reloading
    if (window.history.pushState) {
      const newurl = window.location.protocol + '//' + window.location.host + window.location.pathname.split('/').slice(0, -1).join('/') + '/' + lowerName;
      window.history.pushState({ path: newurl }, '', newurl);
    }

    // Hide user select after setting up
    setShowUserSelect(false);
    setCurrentView('home');
  };

  // Save completed exercises to localStorage
  useEffect(() => {
    if (userName) {
      localStorage.setItem(`${userName}_completedExercises`, JSON.stringify(completedExercises));
    }
  }, [completedExercises, userName]);

  // Save workout history to localStorage
  useEffect(() => {
    if (userName) {
      localStorage.setItem(`${userName}_workoutHistory`, JSON.stringify(workoutHistory));
    }
  }, [workoutHistory, userName]);

  // Save workout plans to localStorage when they change
  useEffect(() => {
    if (userName && workoutPlans.length > 0) {
      localStorage.setItem(`${userName}_plans`, JSON.stringify(workoutPlans));
    }
  }, [workoutPlans, userName]);

  // Workout completion handler
  const toggleExerciseCompletion = (exerciseId) => {
    setCompletedExercises(prev => {
      const newState = {...prev};
      newState[exerciseId] = !newState[exerciseId];
      return newState;
    });
  };

  const reloadWorkoutPlans = () => {
    if (userName) {
      // Confirm before resetting
      if (window.confirm('Dies lädt die neuesten Workout-Pläne und überschreibt deine aktuellen Pläne. Dein Trainingsfortschritt bleibt erhalten. Fortfahren?')) {
        // Determine which workout set to load based on username
        let freshWorkoutPlans;

        if (userName.toLowerCase() === 'romy') {
          freshWorkoutPlans = romyWorkoutPlans; // Original plans from source code
        } else if (userName.toLowerCase() === 'david') {
          freshWorkoutPlans = davidWorkoutPlans; // Original plans from source code
        } else {
          // For other users, default to Romy's plan
          freshWorkoutPlans = romyWorkoutPlans;
        }

        // Update state
        setWorkoutPlans(freshWorkoutPlans);

        // Save to localStorage
        localStorage.setItem(`${userName}_plans`, JSON.stringify(freshWorkoutPlans));

        // Show confirmation message
        alert('Workout-Pläne wurden aktualisiert!');
      }
    } else {
      alert('Bitte wähle zuerst einen Benutzer aus.');
    }
  }

  // Manually finish workout
  const finishWorkout = () => {
    if (selectedPlan) {
      const today = new Date().toISOString().split('T')[0];
      setWorkoutHistory(prev => {
        const newHistory = {...prev};
        newHistory[today] = [...(newHistory[today] || []), selectedPlan.id];
        return newHistory;
      });

      // Reset completion for all exercises
      const exercisesToReset = [
        ...selectedPlan.exercises.map(ex => ex.id),
        selectedPlan.bonusExercise ? selectedPlan.bonusExercise.id : null,
        selectedPlan.bonusExercise2 ? selectedPlan.bonusExercise2.id : null
      ].filter(Boolean);

      setCompletedExercises(prev => {
        const newState = {...prev};
        exercisesToReset.forEach(id => {
          newState[id] = false;
        });
        return newState;
      });

      // Show confetti and celebration
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
        setCurrentView('tracker');
      }, 3000);
    }
  };

  // Reset today's workout
  const resetWorkout = () => {
    if (selectedPlan) {
      const exercisesToReset = [
        ...selectedPlan.exercises.map(ex => ex.id),
        selectedPlan.bonusExercise ? selectedPlan.bonusExercise.id : null,
        selectedPlan.bonusExercise2 ? selectedPlan.bonusExercise2.id : null
      ].filter(Boolean);

      setCompletedExercises(prev => {
        const newState = {...prev};
        exercisesToReset.forEach(id => {
          newState[id] = false;
        });
        return newState;
      });
    }
  };

  // Toggle edit mode for workout plans
  const toggleEditMode = () => {
    setEditMode(!editMode);
    setEditingExercise(null);
  };

  // Save edited exercise
  // Save edited exercise with forced refresh
  const saveExerciseEdit = (exerciseId, updatedExercise) => {
    // Update the workout plans
    setWorkoutPlans(currentPlans => {
      const newPlans = currentPlans.map(plan => {
        // Check in main exercises
        const updatedExercises = plan.exercises.map(ex =>
            ex.id === exerciseId ? {...ex, ...updatedExercise} : ex
        );

        // Check in bonus exercise
        let updatedBonusExercise = plan.bonusExercise;
        if (plan.bonusExercise && plan.bonusExercise.id === exerciseId) {
          updatedBonusExercise = {...plan.bonusExercise, ...updatedExercise};
        }

        return {
          ...plan,
          exercises: updatedExercises,
          bonusExercise: updatedBonusExercise
        };
      });

      // Save to localStorage immediately
      if (userName) {
        localStorage.setItem(`${userName}_plans`, JSON.stringify(newPlans));
      }

      return newPlans;
    });

    // Close the edit form
    setEditingExercise(null);

    // Force a re-render by toggling the edit mode off then on again
    setEditMode(false);
    setTimeout(() => {
      if (selectedPlan) {
        // Re-select the current plan to refresh the UI
        const currentPlanId = selectedPlan.id;
        setSelectedPlan(null);
        setTimeout(() => {
          const refreshedPlan = workoutPlans.find(p => p.id === currentPlanId);
          setSelectedPlan(refreshedPlan);
        }, 10);
      }
    }, 10);
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setUpUserProfile(user);
  };

  // Generate cells for the tracker
  const generateTrackerCells = () => {
    const cells = [];
    const today = new Date();

    // Generate cells for the last 4 weeks (28 days)
    for (let i = 0; i < 28; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      const workouts = workoutHistory[dateString] || [];

      // Determine the cell color based on workout count
      let cellColor = 'bg-gray-700';
      if (workouts.length === 1) cellColor = 'bg-green-700';
      if (workouts.length === 2) cellColor = 'bg-green-500';
      if (workouts.length >= 3) cellColor = 'bg-green-400';

      cells.unshift(
          <div
              key={dateString}
              className={`w-9 h-9 ${cellColor} flex items-center justify-center rounded-md shadow-sm`}
              title={`${dateString}: ${workouts.length} Workout(s)`}
          >
            {workouts.length > 0 && (
                <span className="text-xs font-bold text-white">{workouts.length}</span>
            )}
          </div>
      );
    }

    return cells;
  };

  // Render Home View
  const renderHomeView = () => (
      <div className="space-y-6">
        <div className="p-4 bg-blue-50 rounded-lg shadow text-center">
          <h2 className="text-xl font-bold mb-2 text-blue-800">Wähle deinen Trainingsplan</h2>
          <p className="text-sm mb-4 text-neutral-600">Jedes Training bringt dich deinem Ziel näher!</p>

          <div className="space-y-4">
            {workoutPlans.map(plan => (
                <button
                    key={plan.id}
                    className="w-full p-4 bg-white border border-blue-200 rounded-lg shadow text-left hover:bg-blue-50 transition flex justify-between items-center"
                    onClick={() => {
                      setSelectedPlan(plan);
                      setCurrentView('workout');
                    }}
                >
                  <div>
                    <h3 className="font-bold text-blue-800">{plan.title}</h3>
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  </div>
                  <ChevronRight className="text-blue-500"/>
                </button>
            ))}
          </div>
        </div>

        <button
            className="w-full p-3 bg-indigo-100 text-indigo-800 rounded-lg flex items-center justify-center"
            onClick={() => setCurrentView('tracker')}
        >
          <Calendar className="mr-2" size={18}/>
          Zeige meinen Trainings-Fortschritt
        </button>
        <div className="mt-8 border-t border-gray-700 pt-6">
          <button
              onClick={reloadWorkoutPlans}
              className="w-full p-3 bg-gray-700 text-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-600 transition-colors"
          >
            <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
              <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Workout-Pläne aktualisieren
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">
            Lädt die neuesten Workout-Pläne ohne Trainingsverlauf zu löschen
          </p>
        </div>
      </div>
  );

  // Render Workout View
  const renderWorkoutView = () => {
    if (!selectedPlan) return <div>Plan wird geladen...</div>;

    return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
                onClick={() => setCurrentView('home')}
                className="p-2 text-indigo-300 hover:text-indigo-100 flex items-center"
            >
              <Home size={18} className="mr-1"/> Zurück
            </button>

            <h2 className="text-lg font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">
              {selectedPlan.title}
            </h2>

            <button
                onClick={resetWorkout}
                className="p-2 text-red-400 hover:text-red-300"
            >
              Reset
            </button>
            {userProfile && userProfile.level === 'Advanced' && (
                <button
                    onClick={toggleEditMode}
                    className={`p-2 rounded-full ml-2 ${editMode ? 'bg-red-600 text-white' : 'bg-indigo-700 text-indigo-300'}`}
                >
                  {editMode ? <X size={18} /> : <Edit size={18} />}
                </button>
            )}
          </div>

          <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-indigo-300 flex items-center">
                <Zap size={16} className="mr-2 text-indigo-400" />
                WARM-UP
              </h3>
              <Clock size={18} className="text-gray-500" />
            </div>
            <ul className="text-sm space-y-1 ml-4 list-disc text-gray-300">
              <li>2 Minuten: Auf der Stelle gehen</li>
              <li>3 Minuten: Schulter-, Arm- und Hüftkreisen</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h3 className="font-bold flex items-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              <Dumbbell size={18} className="mr-2 text-indigo-400" />
              ÜBUNGEN
              <Clock size={16} className="ml-2 text-gray-500" />
            </h3>

            {selectedPlan.exercises.map((exercise) => (
                <div
                    key={exercise.id}
                    className={`p-4 rounded-xl border flex items-center justify-between ${
                        completedExercises[exercise.id]
                            ? 'bg-gradient-to-r from-green-900 to-green-800 border-green-700'
                            : 'bg-gradient-to-r from-gray-800 to-gray-900 border-gray-700 hover:border-indigo-600'
                    } transition-all`}
                >
                  <div
                      className={`flex items-center flex-1 py-2 ${editMode ? '' : 'cursor-pointer'}`}
                      onClick={() => !editMode && toggleExerciseCompletion(exercise.id)}
                      style={{ touchAction: 'manipulation' }}
                  >
                    {!editMode && (
                        <div className="mr-3">
                          {completedExercises[exercise.id] ? (
                              <CheckCircle className="text-green-400" size={28} />
                          ) : (
                              <Circle className="text-gray-600" size={28} />
                          )}
                        </div>
                    )}

                    <div>
                      <h4 className={`font-medium ${completedExercises[exercise.id] ? 'text-green-300' : 'text-gray-200'}`}>
                        {exercise.name}
                      </h4>
                      <p className={`text-sm ${completedExercises[exercise.id] ? 'text-green-500' : 'text-gray-400'}`}>
                        {exercise.sets} Sätze × {exercise.reps} Wdh
                        {exercise.weight && ` • ${exercise.weight}`}
                        {` • ${exercise.target}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex">
                    {editMode ? (
                        <button
                            onClick={() => setEditingExercise(exercise)}
                            className="ml-2 bg-indigo-700 hover:bg-indigo-600 p-3 rounded-full text-indigo-200"
                            style={{ touchAction: 'manipulation' }}
                        >
                          <Edit size={22} />
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowExerciseDetails(exercise)}
                            className={`ml-2 p-3 rounded-full ${
                                completedExercises[exercise.id]
                                    ? 'bg-green-700 hover:bg-green-600 text-green-300'
                                    : 'bg-indigo-900 hover:bg-indigo-800 text-indigo-300'
                            }`}
                            style={{ touchAction: 'manipulation' }}
                        >
                          <Info size={22} />
                        </button>
                    )}
                  </div>
                </div>
            ))}

            {/* Bonus Exercises */}
            {(selectedPlan.bonusExercise || selectedPlan.bonusExercise2) && (
                <div className="mt-6">
                  <div className="text-center mb-2">
                    <span className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold inline-flex items-center shadow-lg">
                      <Trophy size={16} className="mr-1" /> POWER-BOOST
                    </span>
                  </div>

                  {/* First Bonus Exercise */}
                  {selectedPlan.bonusExercise && (
                      <div
                          className={`p-4 rounded-xl border-2 flex items-center justify-between mb-3 ${
                              completedExercises[selectedPlan.bonusExercise.id]
                                  ? 'bg-gradient-to-r from-amber-900 to-amber-800 border-amber-700'
                                  : 'bg-gradient-to-r from-gray-800 to-gray-900 border-amber-700'
                          } transform hover:scale-102 transition-transform shadow-lg`}
                      >
                        <div
                            className="flex items-center flex-1 py-2"
                            onClick={() => toggleExerciseCompletion(selectedPlan.bonusExercise.id)}
                            style={{ touchAction: 'manipulation' }}
                        >
                          <div className="mr-3">
                            {completedExercises[selectedPlan.bonusExercise.id] ? (
                                <CheckCircle className="text-amber-400" size={28} />
                            ) : (
                                <Circle className="text-amber-600" size={28} />
                            )}
                          </div>

                          <div>
                            <h4 className="font-medium text-amber-300">{selectedPlan.bonusExercise.name}</h4>
                            <p className="text-sm text-amber-500">
                              {selectedPlan.bonusExercise.sets} Sätze × {selectedPlan.bonusExercise.reps} Wdh • {selectedPlan.bonusExercise.target}
                            </p>
                          </div>
                        </div>

                        <button
                            onClick={() => setShowExerciseDetails(selectedPlan.bonusExercise)}
                            className="ml-2 bg-amber-800 hover:bg-amber-700 p-3 rounded-full text-amber-300"
                            style={{ touchAction: 'manipulation' }}
                        >
                          <Info size={22} />
                        </button>
                      </div>
                  )}

                  {/* Second Bonus Exercise */}
                  {selectedPlan.bonusExercise2 && (
                      <div
                          className={`p-4 rounded-xl border-2 flex items-center justify-between ${
                              completedExercises[selectedPlan.bonusExercise2.id]
                                  ? 'bg-gradient-to-r from-amber-900 to-amber-800 border-amber-700'
                                  : 'bg-gradient-to-r from-gray-800 to-gray-900 border-amber-700'
                          } transform hover:scale-102 transition-transform shadow-lg`}
                      >
                        <div
                            className="flex items-center flex-1 py-2"
                            onClick={() => toggleExerciseCompletion(selectedPlan.bonusExercise2.id)}
                            style={{ touchAction: 'manipulation' }}
                        >
                          <div className="mr-3">
                            {completedExercises[selectedPlan.bonusExercise2.id] ? (
                                <CheckCircle className="text-amber-400" size={28} />
                            ) : (
                                <Circle className="text-amber-600" size={28} />
                            )}
                          </div>

                          <div>
                            <h4 className="font-medium text-amber-300">{selectedPlan.bonusExercise2.name}</h4>
                            <p className="text-sm text-amber-500">
                              {selectedPlan.bonusExercise2.sets} Sätze × {selectedPlan.bonusExercise2.reps} Wdh • {selectedPlan.bonusExercise2.target}
                            </p>
                          </div>
                        </div>

                        <button
                            onClick={() => setShowExerciseDetails(selectedPlan.bonusExercise2)}
                            className="ml-2 bg-amber-800 hover:bg-amber-700 p-3 rounded-full text-amber-300"
                            style={{ touchAction: 'manipulation' }}
                        >
                          <Info size={22} />
                        </button>
                      </div>
                  )}
                </div>
            )}

            {/* Finish Workout Button */}
            <div className="mt-6">
              <button
                  onClick={finishWorkout}
                  disabled={!selectedPlan.exercises.every(ex => completedExercises[ex.id])}
                  className={`w-full py-4 rounded-xl text-white text-lg font-bold shadow-xl flex items-center justify-center transition-all transform hover:scale-105
                ${selectedPlan.exercises.every(ex => completedExercises[ex.id])
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                      : 'bg-gray-700 cursor-not-allowed'}`}
                  style={{ touchAction: 'manipulation' }}
              >
                <Trophy className="mr-2" size={22} />
                WORKOUT COMPLETE!
              </button>
              {!selectedPlan.exercises.every(ex => completedExercises[ex.id]) && (
                  <p className="text-sm text-gray-400 text-center mt-2">
                    Schließe alle Hauptübungen ab, um das Training zu beenden
                  </p>
              )}
            </div>
          </div>

          <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
            <h3 className="font-bold flex items-center mb-3 text-indigo-300">
              <Activity size={16} className="mr-2 text-indigo-400" />
              COOL-DOWN
              <Clock size={16} className="ml-2 text-gray-500" />
            </h3>
            <ul className="text-sm space-y-1 ml-4 list-disc text-gray-300">
              <li>2 Minuten leichtes Gehen</li>
              <li>3 Minuten sanftes Dehnen</li>
            </ul>
          </div>
        </div>
    );
  };

  // Render Tracker View
  const renderTrackerView = () => {
    const streakDays = Object.keys(workoutHistory).length;
    const thisWeekWorkouts = calculateThisWeekWorkouts();

    return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
                onClick={() => setCurrentView('home')}
                className="p-2 text-indigo-300 hover:text-indigo-100 flex items-center"
            >
              <Home size={18} className="mr-1" /> Zurück
            </button>

            <h2 className="text-lg font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">
              DEINE ERFOLGE
            </h2>

            <div className="w-8"></div> {/* Spacer for alignment */}
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl shadow-xl border border-gray-700">
            <h3 className="font-bold text-lg mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">
              DEINE STATISTIK
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-indigo-900 to-indigo-800 p-4 rounded-xl shadow-lg border border-indigo-700 text-center">
                <p className="text-xs text-indigo-400 uppercase tracking-wider">Trainings gesamt</p>
                <p className="text-2xl font-bold text-indigo-200">{countTotalWorkouts()}</p>
              </div>

              <div className="bg-gradient-to-br from-green-900 to-green-800 p-4 rounded-xl shadow-lg border border-green-700 text-center">
                <p className="text-xs text-green-400 uppercase tracking-wider">Diese Woche</p>
                <p className="text-2xl font-bold text-green-200">{thisWeekWorkouts}</p>
              </div>

              <div className="bg-gradient-to-br from-purple-900 to-purple-800 p-4 rounded-xl shadow-lg border border-purple-700 text-center">
                <p className="text-xs text-purple-400 uppercase tracking-wider">Aktive Tage</p>
                <p className="text-2xl font-bold text-purple-200">{streakDays}</p>
              </div>

              <div className="bg-gradient-to-br from-amber-900 to-amber-800 p-4 rounded-xl shadow-lg border border-amber-700 text-center">
                <p className="text-xs text-amber-400 uppercase tracking-wider">Längste Serie</p>
                <p className="text-2xl font-bold text-amber-200">{calculateLongestStreak()}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl shadow-xl border border-gray-700">
            <h3 className="font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">
              DEINE LETZTEN 4 WOCHEN
            </h3>
            <div className="flex flex-wrap justify-center gap-1 mb-3">
              {generateTrackerCells()}
            </div>
            <div className="mt-4 flex justify-center text-xs text-gray-400">
              <div className="flex items-center mr-3">
                <div className="w-3 h-3 bg-gray-700 mr-1 rounded-sm"></div>
                <span>Kein Training</span>
              </div>
              <div className="flex items-center mr-3">
                <div className="w-3 h-3 bg-green-700 mr-1 rounded-sm"></div>
                <span>1 Training</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 mr-1 rounded-sm"></div>
                <span>2+ Trainings</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-900 to-amber-800 p-5 rounded-xl shadow-xl border border-amber-700">
            <h3 className="font-bold flex items-center mb-2 text-amber-300">
              <Award className="mr-2 text-amber-400" size={18} />
              POWER FACT
            </h3>
            <p className="text-sm text-amber-200">
              Konsequentes Training (2-3× pro Woche) verbessert deine Kraft und Ausdauer nicht nur körperlich, sondern stärkt auch dein mentales Wohlbefinden und Selbstvertrauen.
            </p>
          </div>
        </div>
    );
  };

  // Helper function to count total workouts
  const countTotalWorkouts = () => {
    return Object.values(workoutHistory).reduce((total, day) => total + day.length, 0);
  };

  // Helper function to calculate workouts this week
  const calculateThisWeekWorkouts = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday is 0, Monday is 1, etc.
    startOfWeek.setHours(0, 0, 0, 0);

    return Object.entries(workoutHistory).reduce((count, [dateStr, workouts]) => {
      const date = new Date(dateStr);
      if (date >= startOfWeek && date <= today) {
        return count + workouts.length;
      }
      return count;
    }, 0);
  };

  // Helper function to calculate longest streak
  const calculateLongestStreak = () => {
    // Simple implementation - can be enhanced for more accuracy
    return Math.min(5, Object.keys(workoutHistory).length);
  };

  // Render Exercise Detail Modal
  const renderExerciseDetailModal = () => {
    if (!showExerciseDetails) return null;

    const isBonus = selectedPlan?.bonusExercise?.id === showExerciseDetails.id;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl border border-gray-700">
            <div className="p-4 border-b border-gray-700">
              <h3 className={`font-bold text-lg ${isBonus ? 'text-amber-300' : 'text-indigo-300'}`}>
                {showExerciseDetails.name}
              </h3>
              <p className="text-sm text-gray-400">
                Schwierigkeit: {showExerciseDetails.difficulty} • Zielbereich: {showExerciseDetails.target}
              </p>
            </div>

            <div className="p-4">
              <h4 className="font-medium mb-2 text-gray-300">Ausführung:</h4>
              <ol className="list-decimal ml-5 space-y-2 text-gray-300">
                {showExerciseDetails.instructions.map((step, index) => (
                    <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="p-4 border-t border-gray-700 bg-gray-800 flex justify-end">
              <button
                  onClick={() => setShowExerciseDetails(null)}
                  className={`px-5 py-3 text-white rounded-lg text-lg font-medium ${
                      isBonus
                          ? 'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500'
                  }`}
                  style={{ touchAction: 'manipulation' }}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
    );
  };

  // Confetti component
  const Confetti = () => {
    const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
    const pieces = [];

    // Create confetti pieces
    for (let i = 0; i < 100; i++) {
      const style = {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 50}%`,
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        width: `${5 + Math.random() * 10}px`,
        height: `${5 + Math.random() * 10}px`,
        transform: `rotate(${Math.random() * 360}deg)`,
        opacity: Math.random(),
        animation: `fall-${i % 3} ${1 + Math.random() * 2}s ease-in-out forwards`,
      };

      pieces.push(
          <div
              key={i}
              className="absolute rounded-sm"
              style={style}
          />
      );
    }

    return (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          <style jsx>{`
          @keyframes fall-0 {
            0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
          }
          @keyframes fall-1 {
            0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(-360deg); opacity: 0; }
          }
          @keyframes fall-2 {
            0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `}</style>
          {pieces}
        </div>
    );
  };

  // Celebration modal
  const CelebrationModal = () => (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-40 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 text-center max-w-sm w-full transform scale-110 animate-bounce shadow-2xl border border-green-500">
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <Trophy size={80} className="text-yellow-400 animate-pulse" />
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-400 text-white rounded-full p-2 shadow-lg">
                <Zap size={20} />
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">
            WORKOUT CRUSHED!
          </h2>
          <p className="mb-4 text-gray-300">Du hast es durchgezogen! Sei stolz auf dich! 💪</p>
          <p className="text-sm text-green-400">Dein Körper dankt es dir!</p>
        </div>
      </div>
  );

  const renderUserSelection = () => {
    return (
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 w-full max-w-md shadow-2xl border border-gray-700">
            <h2 className="text-xl font-bold mb-6 text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-400">
              WÄHLE DEIN PROFIL
            </h2>

            <div className="space-y-4">
              <button
                  onClick={() => handleUserSelect('romy')}
                  className="w-full p-5 bg-gradient-to-r from-purple-700 to-purple-800 rounded-xl text-white hover:from-purple-600 hover:to-purple-700 transition-all flex items-center"
              >
                <div className="bg-purple-900 p-3 rounded-full mr-4">
                  <User size={24} className="text-purple-200" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-lg">Romy</h3>
                  <p className="text-sm text-purple-200">Ziel: Gewichtsreduktion</p>
                </div>
              </button>

              <button
                  onClick={() => handleUserSelect('david')}
                  className="w-full p-5 bg-gradient-to-r from-blue-700 to-blue-800 rounded-xl text-white hover:from-blue-600 hover:to-blue-700 transition-all flex items-center"
              >
                <div className="bg-blue-900 p-3 rounded-full mr-4">
                  <User size={24} className="text-blue-200" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-lg">David</h3>
                  <p className="text-sm text-blue-200">Ziel: Strength Training</p>
                </div>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-gray-800 text-gray-400 text-sm">oder</span>
                </div>
              </div>

              <div className="pt-2">
                <label className="text-gray-300 block mb-2 text-sm">Eigener Name</label>
                <div className="flex">
                  <input
                      type="text"
                      placeholder="Dein Name"
                      className="flex-1 p-3 rounded-l-lg bg-gray-700 text-white border border-gray-600"
                      id="custom-name"
                  />
                  <button
                      onClick={() => {
                        const name = document.getElementById('custom-name').value;
                        if (name.trim()) handleUserSelect(name);
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-r-lg"
                  >
                    Los!
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
    );
  };

  // Main render
  return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 min-h-screen text-gray-100">
        <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 shadow-lg">
          <h1 className="text-xl font-bold text-center flex items-center justify-center">
            <Dumbbell size={24} className="mr-2 animate-pulse" />
            {userProfile && <span>• {userProfile.name}'s</span>}&nbsp;POWER TRAINING
          </h1>
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        </header>

        <main className="container mx-auto p-4 max-w-md">
          {showUserSelect ? renderUserSelection() : (
              <>
                {currentView === 'home' && renderHomeView()}
                {currentView === 'workout' && renderWorkoutView()}
                {currentView === 'tracker' && renderTrackerView()}
                {renderExerciseDetailModal()}
                {showConfetti && <Confetti />}
                {showConfetti && <CelebrationModal />}
                {editingExercise && (
                    <ExerciseEditForm
                        exercise={editingExercise}
                        onSave={saveExerciseEdit}
                        onCancel={() => setEditingExercise(null)}
                    />
                )}
              </>
          )}
        </main>

        <footer className="bg-gray-800 border-t border-gray-700 p-4 text-center text-gray-400 text-sm mt-8">
          <p>Du schaffst das! 💪</p>
        </footer>
      </div>
  );
};

export default WorkoutApp;